import { type FC } from 'react';

interface CustomerProposalExpiredProps {
  logoUrl?: string;
  expirationDate: Date;
}

export const CustomerProposalExpired: FC<CustomerProposalExpiredProps> = ({
  logoUrl,
  expirationDate,
}) => {
  const handleContact = () => {
    window.location.href =
      'mailto:office@kalosflorida.com?subject=Expired Proposal - Need Updated Quote';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logo */}
      <div className="bg-white py-6">
        <div className="text-center">
          {logoUrl && <img src={logoUrl} alt="Kalos Services" className="mx-auto h-12 w-auto" />}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-4xl">
          {/* Large Red Box */}
          <div
            className="rounded-lg border border-red-200 px-16 py-8 text-center"
            style={{ backgroundColor: 'rgb(254 242 242)' }}
          >
            {/* Red X Icon */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            {/* Expired Message - directly on red background */}
            <h1 className="mb-4 text-3xl font-bold text-gray-900">Proposal Expired</h1>
            <p className="mb-8 text-lg text-gray-700">
              This proposal expired on {expirationDate.toLocaleDateString()}
            </p>

            {/* Small White Box inside Red Box */}
            <div className="mx-8 mb-12 rounded-lg bg-white p-8 text-gray-700">
              <p className="mb-4">
                Unfortunately, this proposal is no longer valid as it has passed its expiration
                date. We would be happy to create an updated proposal for you with current pricing
                and availability.
              </p>
              <p className="mb-4">
                Please contact us to request a new proposal or if you have any questions about our
                services.
              </p>
              <p className="text-sm text-amber-600">
                Note: If you had previously signed this proposal, please contact us to discuss your
                options.
              </p>
            </div>

            {/* Action Buttons - on red background */}
            <div className="flex flex-col justify-center gap-4 px-4 pb-8 pt-4 sm:flex-row">
              <button
                onClick={handleContact}
                className="inline-flex items-center justify-center rounded-md bg-red-600 px-6 py-3 font-medium text-white transition-colors hover:bg-red-700"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Us for New Proposal
              </button>
              <a
                href="https://customer.kalosflorida.com"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-white py-6">
        <div className="text-center text-gray-600">
          <p className="mb-2">If you have any questions, please contact us at:</p>
          <p>
            <a href="mailto:office@kalosflorida.com" className="text-blue-600 hover:text-blue-800">
              office@kalosflorida.com
            </a>
            {' | '}
            <a href="tel:(352)243-7099" className="text-blue-600 hover:text-blue-800">
              (352) 243-7099
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 py-3 text-center text-white">
        <p className="text-sm">©2025 Kalos Services, Inc. All rights reserved.</p>
      </div>
    </div>
  );
};
